import React from 'react';
import i18n from 'i18next';
import classNames from 'classnames';
import LinkedIn from '@mui/icons-material/LinkedIn';
import Link from '@mui/icons-material/Link';

import { Avatar } from 'uikit/avatars';
import { Typo } from 'uikit/content';
import { Button } from 'uikit/buttons';
import { Insignias } from 'uikit/insignias';

import styles from '../styles/profileHero.module.scss';

const ProfileHero = ({
  profile,
  isCurrentUser,
  isReputationEnabled,
  isLoading,
  onEditTopicsClick,
  onTopicsFollowedClick,
  className,
}) => {
  if (isLoading) return;

  const { followed_topics_count: followedTopicsCount } = profile;

  const containerClassName = className === 'profileModal' ? styles.modal : className;

  const titles = [profile.company_name, profile.job_title].filter((item) => item).join(', ');

  const insignias = [];

  if (isReputationEnabled) {
    insignias.push({
      ariaLabel: i18n.t('count-reputation', { count: profile.reputation_points }),
      icon: 'reputation',
      text: profile.reputation_points ? profile.reputation_points.toString() : '0',
      tooltip: i18n.t('reputation'),
    });
  }

  if (profile.credentials_count) {
    insignias.push({
      ariaLabel: i18n.t('count-credentials', { count: profile.credentials_count }),
      icon: 'credential',
      text: profile.credentials_count ? profile.credentials_count.toString() : '0',
      tooltip: i18n.t('credentials'),
    });
  }

  if (profile.trophies_count) {
    insignias.push({
      ariaLabel: i18n.t('count-trophies', { count: profile.trophies_count }),
      icon: 'trophy',
      text: profile.trophies_count ? profile.trophies_count.toString() : '0',
      tooltip: i18n.t('trophies'),
    });
  }

  return (
    <div className={classNames(styles.container, containerClassName)}>
      <div className={styles.background}>
        <div
          className={styles.backgroundImage}
          style={
            profile.picture
              ? {
                  backgroundImage: `url(${profile.picture.w3200})`,
                }
              : {}
          }
        />
      </div>
      <div className={styles.content}>
        <div className={styles.inner}>
          <div className={styles.left}>
            <Avatar
              avatar={profile.picture && profile.picture.w550}
              className={styles.avatar}
              name={profile.full_name}
              size="fluid"
            />
          </div>
          <div className={styles.right}>
            <div className={styles.heading}>
              <div className={styles.headings}>
                {titles && (
                  <div className={styles.titles}>
                    <Typo className={styles.titlesText} element="span" variant="s02">
                      {titles}
                    </Typo>
                  </div>
                )}
                <Typo className={styles.name} element="h1" variant="d07">
                  {`${profile.first_name} ${profile.last_name}`}
                </Typo>
                {!!insignias?.length && (
                  <div className={styles.insigniasWrap}>
                    <Insignias
                      className={styles.insignias}
                      insigniasIconSize={18}
                      insignias={insignias}
                    />
                  </div>
                )}
              </div>
            </div>
            {profile.description && (
              <div className={styles.description}>
                <Typo className={styles.descriptionText} variant="b02">
                  {profile.description}
                </Typo>
              </div>
            )}
            {(profile.twitter_handle || profile.linkedin_url || profile.website_url) && (
              <ul className={styles.links}>
                {profile.twitter_handle && (
                  <li className={styles.linksItem}>
                    <Button
                      className={styles.button}
                      color="secondary"
                      icon="twitter"
                      size="xsmall"
                      target="_blank"
                      text={`@${profile.twitter_handle}`}
                      to={`https://twitter.com/${profile.twitter_handle}`}
                      variant="link"
                      isExternalLink
                    />
                  </li>
                )}
                {profile.linkedin_url && (
                  <li className={styles.linksItem}>
                    <Button
                      className={styles.button}
                      color="secondary"
                      icon={<LinkedIn />}
                      size="xsmall"
                      target="_blank"
                      text={profile.linkedin_url}
                      to={profile.linkedin_url}
                      variant="link"
                      isExternalLink
                    />
                  </li>
                )}
                {profile.website_url && (
                  <li className={styles.linksItem}>
                    <Button
                      className={styles.button}
                      color="secondary"
                      icon={<Link />}
                      size="xsmall"
                      target="_blank"
                      text={profile.website_url}
                      to={profile.website_url}
                      variant="link"
                      isExternalLink
                    />
                  </li>
                )}
              </ul>
            )}
            {isCurrentUser && profile.can_follow_topics && (
              <div className={styles.topics}>
                <Button
                  className={styles.button}
                  color="secondary"
                  size="small"
                  text={i18n.t('count-topics-followed', { count: followedTopicsCount })}
                  variant="soft"
                  onClick={onTopicsFollowedClick}
                />
                <Button
                  className={styles.editTopics}
                  color="secondary"
                  size="small"
                  text={i18n.t('edit-topics')}
                  variant="link"
                  onClick={onEditTopicsClick}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ProfileHero);
